import type { InternalNode, Node, Position } from '@xyflow/react';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import type { LayoutCenter } from './flowchart-edge-node-model';
import { toNodeRect } from './flowchart-edge-node-model';
import { isBackEdge, type NodeRect, type Point } from './flowchart-edge-geometry';
import { resolveFlowchartEdgeGeometry } from './flowchart-edge-routing';
import {
  resolveEdgeRole,
  resolveFlowchartEdgeVisualState,
  type FlowchartEdgeActivityInput,
  type FlowchartEdgeVisualState,
} from './flowchart-edge-style';

interface FlowchartEdgeNodeRects {
  sourceRect: NodeRect;
  targetRect: NodeRect;
}

interface FlowchartEdgeMarkerIds {
  endId: string;
  startId: string;
}

export interface FlowchartEdgeRenderModel {
  edgeLabel: string | null;
  endId: string;
  endMarker: FlowchartEdgeData['endMarker'];
  labelPosition: Point;
  path: string;
  startId: string;
  startMarker: FlowchartEdgeData['startMarker'];
  visualState: FlowchartEdgeVisualState;
}

interface FlowchartEdgeRenderModelInput extends FlowchartEdgeActivityInput {
  data: FlowchartEdgeData | undefined;
  id: string;
  layoutCenter: LayoutCenter;
  sourceNode: InternalNode<Node<FlowchartNodeData>> | undefined;
  sourcePosition: Position;
  targetNode: InternalNode<Node<FlowchartNodeData>> | undefined;
  targetPosition: Position;
}

function resolveNodeRects(input: {
  sourceNode: InternalNode<Node<FlowchartNodeData>> | undefined;
  targetNode: InternalNode<Node<FlowchartNodeData>> | undefined;
}): FlowchartEdgeNodeRects | null {
  if (!input.sourceNode || !input.targetNode) {
    return null;
  }

  const sourceRect = toNodeRect(input.sourceNode);
  const targetRect = toNodeRect(input.targetNode);
  if (!sourceRect || !targetRect) {
    return null;
  }

  return { sourceRect, targetRect };
}

function resolveIsSelfLoop(input: {
  selfLoopRadius: number;
  source: string;
  target: string;
}): boolean {
  return input.source === input.target || input.selfLoopRadius > 0;
}

function resolveIsBack(input: {
  isSelfLoop: boolean;
  parallelOffset: number;
  sourcePosition: Position;
  sourceRect: NodeRect;
  targetRect: NodeRect;
}): boolean {
  return !input.isSelfLoop &&
    input.parallelOffset === 0 &&
    isBackEdge({
      source: input.sourceRect,
      target: input.targetRect,
      sourcePosition: input.sourcePosition,
    });
}

function buildMarkerIds(id: string): FlowchartEdgeMarkerIds {
  return {
    endId: `fc-me-${id}`,
    startId: `fc-ms-${id}`,
  };
}

export function resolveFlowchartEdgeRenderModel(
  input: FlowchartEdgeRenderModelInput,
): FlowchartEdgeRenderModel | null {
  if (!input.data) {
    return null;
  }

  const rects = resolveNodeRects({
    sourceNode: input.sourceNode,
    targetNode: input.targetNode,
  });
  if (!rects) {
    return null;
  }

  const { sourceNode } = input;
  if (!sourceNode) {
    return null;
  }

  const isSelfLoop = resolveIsSelfLoop({
    selfLoopRadius: input.data.selfLoopRadius,
    source: input.source,
    target: input.target,
  });
  const isBack = resolveIsBack({
    isSelfLoop,
    parallelOffset: input.data.parallelOffset,
    sourcePosition: input.sourcePosition,
    sourceRect: rects.sourceRect,
    targetRect: rects.targetRect,
  });
  const edgeRole = resolveEdgeRole({
    kind: input.data.kind,
    sourceRole: sourceNode.data.role,
    sourceShape: sourceNode.data.shape,
    isBack,
  });
  const geometry = resolveFlowchartEdgeGeometry({
    sourceRect: rects.sourceRect,
    targetRect: rects.targetRect,
    sourcePosition: input.sourcePosition,
    targetPosition: input.targetPosition,
    isSelfLoop,
    selfLoopRadius: input.data.selfLoopRadius,
    parallelOffset: input.data.parallelOffset,
    isBack,
    layoutCenter: input.layoutCenter,
  });
  const markerIds = buildMarkerIds(input.id);

  return {
    edgeLabel: input.data.edgeLabel,
    endId: markerIds.endId,
    endMarker: input.data.endMarker,
    labelPosition: geometry.labelPosition,
    path: geometry.path,
    startId: markerIds.startId,
    startMarker: input.data.startMarker,
    visualState: resolveFlowchartEdgeVisualState({
      activity: {
        focusedNodeId: input.focusedNodeId,
        focusedNodeIds: input.focusedNodeIds,
        hoveredConnectedNodeIds: input.hoveredConnectedNodeIds,
        hoveredNodeId: input.hoveredNodeId,
        source: input.source,
        target: input.target,
      },
      edgeRole,
      endMarker: input.data.endMarker,
      endMarkerId: markerIds.endId,
      kind: input.data.kind,
      startMarker: input.data.startMarker,
      startMarkerId: markerIds.startId,
    }),
  };
}
